import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { errorTextViaYupOrRailsOrJoi } from "@shared/lib/formHelpers";

interface Props {
  name: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  label?: string;
  description?: string;
  onChange?: (_name: string, _value: string) => void;
  className?: string;
  inputType?: string;
  pretext?: string;
  value?: any;
  autoFocus?: boolean;
  defaultValue?: string;
  errors?: any;
  requiredLabel?: string;
  autoComplete?: string;
  autoCapitalize?: string; // none|sentences|words|characters
  onKeyDown?: (_event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (_name: string, _value: string) => void;
  children?: React.ReactNode;
  darkModeAllowed?: boolean;
}

const SessionFormInput: React.FC<Props> = (props) => {
  const {
    errors,
    label,
    isRequired = false,
    isDisabled = false,
    onKeyDown,
    defaultValue,
    requiredLabel = "required",
    darkModeAllowed = true,
  } = props;

  const errorText = errorTextViaYupOrRailsOrJoi({ errors, name: props.name });
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    const handler = (event: KeyboardEvent) => {
      if ((event as any).keyCode === 27 || (event as any).key === "Escape") {
        node.blur();
      }
    };
    node.addEventListener("keydown", handler);
    return () => node.removeEventListener("keydown", handler);
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (props.onChange) props.onChange(props.name, event.currentTarget.value);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (props.onBlur) props.onBlur(props.name, event.currentTarget.value);
  };

  const inputId = props.label
    ? `input${props.label.replace(/\s+/g, "")}`
    : props.name
    ? props.name
    : "Default";

  return (
    <Styled.Container darkModeAllowed={darkModeAllowed}>
      {label && (
        <Styled.LabelWrapper>
          <Styled.LabelContainer>
            <Styled.LabelText darkModeAllowed={darkModeAllowed}>
              <Styled.Label htmlFor={inputId}>{label}</Styled.Label>
              {isRequired && <Styled.RequiredLabel darkModeAllowed={darkModeAllowed}>({requiredLabel})</Styled.RequiredLabel>}
            </Styled.LabelText>
            {errorText && <Styled.Error darkModeAllowed={darkModeAllowed}>{errorText}</Styled.Error>}
          </Styled.LabelContainer>
        </Styled.LabelWrapper>
      )}
      {props.description && <p>{props.description}</p>}
      <Styled.UI hasPretext={Boolean(props.pretext)} hasError={Boolean(errorText)} darkModeAllowed={darkModeAllowed}>
        {props.pretext && <Styled.Pretext darkModeAllowed={darkModeAllowed}>{props.pretext}</Styled.Pretext>}
        <input
          id={inputId}
          ref={inputRef}
          type={props.inputType || "text"}
          value={props.value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onBlur={handleBlur}
          name={props.name}
          placeholder={props.placeholder}
          autoFocus={props.autoFocus}
          disabled={isDisabled}
          onKeyDown={onKeyDown}
          autoComplete={props.autoComplete}
          autoCapitalize={props.autoCapitalize}
        />
      </Styled.UI>
      {props.children}
    </Styled.Container>
  );
};

export default SessionFormInput;

/* Styled Components (mobile-first for session forms) */
let Styled: any = {};

Styled.Container = styled.div<{ darkModeAllowed?: boolean }>((props: any) => {
  const t: any = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    label: SessionFormInput;
    ${t.mb(5)}
    > p {
      ${[t.text.sm, t.mt(-1), t.mb(2)]};
      color: ${darkModeAllowed && t.dark ? t.color.gray[400] : t.color.gray[600]};
      max-width: 32rem;
      a {
        ${[t.text.medium]}
        color: ${darkModeAllowed && t.dark ? t.color.gray[200] : t.color.black};
        &:hover {
          text-decoration: underline;
        }
      }
      ${t.mq["md"]} {
        ${t.text.xs}
      }
    }
  `;
});

Styled.UI = styled.div<{ hasError?: boolean; hasPretext?: boolean; darkModeAllowed?: boolean }>((props: any) => {
  const t: any = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    label: SessionFormInput_UI;
    display: flex;
    input {
      ${[t.h(10), t.px(2), t.rounded.sm, t.text.base]}
      appearance: none;
      color: ${darkModeAllowed && t.dark ? t.color.gray[200] : t.color.black};
      background-color: ${darkModeAllowed && t.dark ? "rgba(255,255,255,0.07)" : t.color.white};
      width: 100%;
      text-overflow: ellipsis;
      border-width: 1px;
      border-style: solid;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
      border-color: ${props.hasError
        ? darkModeAllowed && t.dark
          ? t.color.red[400]
          : t.color.red[500]
        : darkModeAllowed && t.dark
        ? "transparent"
        : t.color.gray[300]};
      ${props.hasPretext ? "border-top-left-radius: 0; border-bottom-left-radius: 0;" : ""}
      &::placeholder {
        color: ${t.color.gray[500]};
      }
      &:hover {
        border-color: ${props.hasError
          ? darkModeAllowed && t.dark
            ? t.color.red[300]
            : t.color.red[600]
          : darkModeAllowed && t.dark
          ? t.color.gray[600]
          : t.color.gray[400]};
      }
      &:focus {
        outline: none;
        border-color: ${props.hasError
          ? darkModeAllowed && t.dark
            ? t.color.red[300]
            : t.color.red[600]
          : darkModeAllowed && t.dark
          ? t.color.gray[500]
          : t.color.gray[400]};
        ${darkModeAllowed && t.dark ? "" : `box-shadow: 0 0 0 2px ${t.color.gray[300]};`}
      }
      &:disabled {
        color: ${darkModeAllowed && t.dark ? t.color.gray[500] : t.color.gray[600]};
        background-color: ${darkModeAllowed && t.dark ? t.color.gray[800] : t.color.gray[100]};
        ${darkModeAllowed && t.dark && `border: 1px solid ${t.color.gray[800]};`}
        border-color: ${darkModeAllowed && t.dark ? "rgba(255,255,255,0.08)" : t.color.gray[300]};
      }
      &:disabled:hover {
        ${!(darkModeAllowed && t.dark) && `border-color: ${t.color.gray[300]};`}
      }
    }
    input[type="number"] {
      appearance: textfield;
    }
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      appearance: none;
      margin: 0;
    }
  `;
});

Styled.Pretext = styled.div<{ darkModeAllowed?: boolean }>((props: any) => {
  const t: any = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    label: SessionFormInput_Pretext;
    ${[t.h(10), t.px(2), t.text.base, t.rounded.sm]}
    color: ${darkModeAllowed && t.dark ? t.color.gray[300] : t.color.gray[600]};
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    border: 1px solid ${darkModeAllowed && t.dark ? "transparent" : t.color.gray[300]};
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    background-color: ${darkModeAllowed && t.dark ? t.color.gray[700] : t.color.gray[100]};
  `;
});

Styled.LabelWrapper = styled.div(() => {
  return css`
    label: SessionFormInput_LabelWrapper;
  `;
});

Styled.LabelContainer = styled.div(() => {
  return css`
    label: SessionFormInput_LabelContainer;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  `;
});

Styled.LabelText = styled.div<{ darkModeAllowed?: boolean }>((props: any) => {
  const t: any = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    label: SessionFormInput_LabelText;
    ${[t.text.sm, t.text.bold]}
    color: ${darkModeAllowed && t.dark ? t.color.gray[300] : t.color.black};
    display: inline-block;
    line-height: 1.4;
    margin-bottom: 0.375rem;

    ${t.mq["md"]} {
      ${[t.text.xs]}
    }
  `;
});

Styled.Label = styled.label(() => {
  return css`
    label: SessionFormInput_Label;
  `;
});

Styled.RequiredLabel = styled.span<{ darkModeAllowed?: boolean }>((props: any) => {
  const t: any = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    label: SessionFormInput_RequiredLabel;
    ${[t.text.normal, t.ml(1)]}
    color: ${darkModeAllowed && t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Error = styled.div<{ darkModeAllowed?: boolean }>((props: any) => {
  const t: any = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    label: SessionFormInput_Error;
    ${t.text.sm}
    color: ${darkModeAllowed && t.dark ? t.color.red[400] : t.color.red[600]};

    ${t.mq["md"]} {
      ${[t.text.xs]}
    }
  `;
});
